/**
 * Icon — the Font Awesome glyph primitive. The host app loads the FA kit
 * globally, so this renders the kit's class-based `<i>` and ships no icon JS.
 * Presentational and hook-free, so it renders as a Server Component.
 *
 * The class string is filtered to `fa-*` tokens only. Icon names reach this
 * component from agent envelopes and other untrusted payloads, where an
 * arbitrary class (`fixed inset-0`) would let the payload rewrite the
 * surrounding layout. The same payload can carry props, so the rest is spread
 * through an allowlist and first, leaving the glyph's own attributes to win.
 *
 * Decorative by default (`aria-hidden`). An `aria-label` exposes the glyph as
 * an image with that name — for an icon-only control or a status dot.
 *
 * `size` has no default and the stylesheet declares no block-level font-size:
 * an unsized glyph inherits from its container, which is what lets IconTile
 * drive it. That is also why the size modifiers hold a font-size each rather
 * than a component custom property — there is no block declaration to point at.
 *
 * Stylesheet note, since the SCSS carries no comments: `--fa-width: 1em` makes
 * the box square. Font Awesome 7 gives every glyph `width: var(--fa-width,
 * 1.25em)`, while the Figma icon frames are square (14/16/20/24), so without it
 * each glyph box is a quarter wider than its size. The sizes are drawn glyph
 * sizes, with no inset: sm 14, md 16, lg 20, xl 24 (Small, Body, Big, Title),
 * and the box is exactly the glyph.
 */

import {type FC} from 'react';
import cn from 'classnames';

import {sizeClass} from '../../shared/classNames';

import type {IconProps} from './types';
import {allowedIconProps, sanitizeFaClasses} from './utils';

import styles from './styles.module.scss';

const Icon: FC<IconProps> = ({
  name,
  size,
  className,
  role,
  'aria-label': ariaLabel,
  ...props
}) => (
  <i
    {...allowedIconProps(props)}
    data-slot="icon"
    data-size={size}
    className={cn(
      styles.icon,
      sanitizeFaClasses(name),
      size && sizeClass(styles, 'icon', size),
      className,
    )}
    aria-hidden={ariaLabel ? undefined : true}
    aria-label={ariaLabel}
    role={ariaLabel ? 'img' : role}
  />
);

export {Icon};
